import React, { useState, useEffect } from 'react';
import { api, apiClient } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Search,
  AlertCircle,
  CheckCircle2,
  Calculator,
  Database,
  RefreshCcw,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import TaxRateSeedData from '@/components/admin/TaxRateSeedData';
import AllStatesTaxSeedData from '@/components/admin/AllStatesTaxSeedData';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

export default function TaxRateManagement() {
  const [configs, setConfigs] = useState([]);
  const [ficaConfigs, setFicaConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterJurisdiction, setFilterJurisdiction] = useState('all');
  
  const [formData, setFormData] = useState({
    tax_year: new Date().getFullYear(),
    jurisdiction: 'federal',
    state_code: '',
    filing_status: 'single',
    standard_deduction: 0,
    tax_brackets: [],
    notes: ''
  });

  const [ficaForm, setFicaForm] = useState({
    tax_year: new Date().getFullYear(),
    social_security_rate: 0.062,
    social_security_wage_base: 168600,
    medicare_rate: 0.0145,
    additional_medicare_rate: 0.009,
    additional_medicare_threshold: 200000,
    notes: ''
  });

  const [hasFederal, setHasFederal] = useState(false);
  const [hasState, setHasState] = useState(false);
  const [ninjasYear, setNinjasYear] = useState(new Date().getFullYear());
  const [ninjasRegions, setNinjasRegions] = useState('federal');
  const [ninjasImporting, setNinjasImporting] = useState(false);
  const [taxFoundationYear, setTaxFoundationYear] = useState(2025);
  const [taxFoundationUrl, setTaxFoundationUrl] = useState('');
  const [taxFoundationImporting, setTaxFoundationImporting] = useState(false);
  const [ficaYear, setFicaYear] = useState(new Date().getFullYear());
  const [ficaSsaUrl, setFicaSsaUrl] = useState('https://www.ssa.gov/oact/cola/cbb.html');
  const [ficaImporting, setFicaImporting] = useState(false);
  const [ficaManualRate, setFicaManualRate] = useState('');
  const [ficaManualWageBase, setFicaManualWageBase] = useState('');
  const [useSsaFallback, setUseSsaFallback] = useState(false);
  const [ninjasOpen, setNinjasOpen] = useState(false);
  const [taxFoundationOpen, setTaxFoundationOpen] = useState(false);
  const [ficaOpen, setFicaOpen] = useState(false);
  const [annualUpdating, setAnnualUpdating] = useState(false);
  const [annualUpdateResult, setAnnualUpdateResult] = useState(null);
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    loadConfigs();
  }, []);

  useEffect(() => {
    if (!configs.length) return;
    const years = [...new Set(configs.map((c) => c.tax_year))].filter((y) => Number.isFinite(y));
    if (!years.length) return;
    const currentYear = new Date().getFullYear();
    const nextYear = years.includes(currentYear) ? String(currentYear) : String(Math.max(...years));
    if (!years.includes(Number(filterYear)) && filterYear !== 'all') {
      setFilterYear(nextYear);
    }
  }, [configs, filterYear]);

  const loadConfigs = async () => {
    try {
      const [taxData, ficaData] = await Promise.all([
        api.entities.TaxRateConfig.list('-tax_year'),
        api.entities.FicaConfig.list('-tax_year')
      ]);
      setConfigs(taxData);
      setFicaConfigs(ficaData);
      
      // Check what data exists
      const federalExists = taxData.some(c => c.jurisdiction === 'federal');
      const stateExists = taxData.some(c => c.jurisdiction === 'state');
      setHasFederal(federalExists);
      setHasState(stateExists);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load tax configurations');
    } finally {
      setLoading(false);
    }
  };

  const findFicaForYear = (year) => ficaConfigs.find((config) => config.tax_year === year);

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData(config);
      const matchingFica = findFicaForYear(config.tax_year);
      setFicaForm({
        tax_year: config.tax_year,
        social_security_rate: matchingFica?.social_security_rate ?? 0.062,
        social_security_wage_base: matchingFica?.social_security_wage_base ?? 168600,
        medicare_rate: matchingFica?.medicare_rate ?? 0.0145,
        additional_medicare_rate: matchingFica?.additional_medicare_rate ?? 0.009,
        additional_medicare_threshold: matchingFica?.additional_medicare_threshold ?? 200000,
        notes: matchingFica?.notes ?? ''
      });
    } else {
      setEditingConfig(null);
      setFormData({
        tax_year: new Date().getFullYear(),
        jurisdiction: 'federal',
        state_code: '',
        filing_status: 'single',
        standard_deduction: 0,
        tax_brackets: [],
        notes: ''
      });
      setFicaForm({
        tax_year: new Date().getFullYear(),
        social_security_rate: 0.062,
        social_security_wage_base: 168600,
        medicare_rate: 0.0145,
        additional_medicare_rate: 0.009,
        additional_medicare_threshold: 200000,
        notes: ''
      });
    }
    setShowModal(true);
  };


  const handleImportApiNinjas = async () => {
    if (!ninjasYear) {
      toast.error('Provide a tax year');
      return;
    }

    setNinjasImporting(true);
    try {
      const response = await apiClient.post('/tax-rates/import-ninjas', {
        year: Number(ninjasYear),
        regions: ninjasRegions
      });
      const { created = 0, updated = 0 } = response.data || {};
      toast.success(`API Ninjas import complete: ${created} created, ${updated} updated`);
      await loadConfigs();
    } catch (err) {
      console.error(err);
      toast.error('Failed to import from API Ninjas');
    } finally {
      setNinjasImporting(false);
    }
  };

  const handleImportTaxFoundation = async () => {
    if (!taxFoundationYear) {
      toast.error('Provide a tax year');
      return;
    }

    setTaxFoundationImporting(true);
    try {
      const response = await apiClient.post('/tax-rates/import-taxfoundation', {
        year: Number(taxFoundationYear),
        downloadUrl: taxFoundationUrl || undefined
      });
      const { created = 0, updated = 0 } = response.data || {};
      toast.success(`Tax Foundation import complete: ${created} created, ${updated} updated`);
      await loadConfigs();
    } catch (err) {
      console.error(err);
      toast.error('Failed to import from Tax Foundation');
    } finally {
      setTaxFoundationImporting(false);
    }
  };

  const handleImportFica = async () => {
    if (!ficaYear || !ficaSsaUrl) {
      toast.error('Provide a tax year and SSA URL');
      return;
    }

    setFicaImporting(true);
    try {
      const response = await apiClient.post('/tax-rates/import-fica', {
        year: Number(ficaYear),
        ssaUrl: useSsaFallback ? ficaSsaUrl : undefined,
        ssRate: ficaManualRate || undefined,
        wageBase: ficaManualWageBase || undefined,
        useSsaFallback,
        filingStatus: 'single'
      });
      const { created, updated } = response.data || {};
      toast.success(created ? 'FICA imported' : updated ? 'FICA updated' : 'FICA import complete');
      await loadConfigs();
    } catch (err) {
      console.error(err);
      toast.error('Failed to import FICA data');
    } finally {
      setFicaImporting(false);
    }
  };


  const handleAnnualUpdate = async () => {
    setAnnualUpdating(true);
    try {
      const response = await apiClient.post('/tax-rates/annual-update', {});
      const { result } = response.data || {};
      setAnnualUpdateResult(result);
      const federalOk = result?.federal && 'created' in result.federal;
      const ficaOk = result?.fica && ('created' in result.fica || 'updated' in result.fica);
      const taxFoundationOk = result?.taxFoundation && 'totalStates' in result.taxFoundation;
      if (federalOk || ficaOk || taxFoundationOk) {
        toast.success(`Annual tax table update complete for ${result.year}`);
      } else {
        toast.warning('Annual tax table update ran but nothing was imported — check the result details below');
      }
      await loadConfigs();
    } catch (err) {
      console.error(err);
      toast.error('Failed to run annual tax table update');
    } finally {
      setAnnualUpdating(false);
    }
  };

  const handleSave = async () => {
    try {
      const ficaPayload = { ...ficaForm, tax_year: formData.tax_year };
      const existingFica = findFicaForYear(formData.tax_year);
      if (editingConfig) {
        await api.entities.TaxRateConfig.update(editingConfig.id, formData);
        if (existingFica) {
          await api.entities.FicaConfig.update(existingFica.id, ficaPayload);
        } else {
          await api.entities.FicaConfig.create(ficaPayload);
        }
        toast.success('Tax configuration updated');
      } else {
        await api.entities.TaxRateConfig.create(formData);
        if (existingFica) {
          await api.entities.FicaConfig.update(existingFica.id, ficaPayload);
        } else {
          await api.entities.FicaConfig.create(ficaPayload);
        }
        toast.success('Tax configuration created');
      }
      setShowModal(false);
      loadConfigs();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save tax configuration');
    }
  };

  const deleteConfig = async (config) => {
    try {
      await api.entities.TaxRateConfig.delete(config.id);
      toast.success('Tax configuration deleted');
      loadConfigs();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete configuration');
    }
  };

  const confirmDeleteConfig = (config) => {
    openConfirm({
      title: 'Delete this tax configuration?',
      description: `Remove the ${config.tax_year} ${config.jurisdiction} ${config.filing_status} rates. Calculations depending on this entry will fall back to defaults until you add a replacement.`,
      confirmLabel: 'Delete configuration',
      isDestructive: true,
      onConfirm: () => deleteConfig(config)
    });
  };

  const addBracket = () => {
    setFormData({
      ...formData,
      tax_brackets: [
        ...formData.tax_brackets,
        { min_income: 0, max_income: 0, rate: 0, base_tax: 0 }
      ]
    });
  };

  const updateBracket = (index, field, value) => {
    const newBrackets = [...formData.tax_brackets];
    newBrackets[index][field] = parseFloat(value) || 0;
    setFormData({ ...formData, tax_brackets: newBrackets });
  };

  const removeBracket = (index) => {
    setFormData({
      ...formData,
      tax_brackets: formData.tax_brackets.filter((_, i) => i !== index)
    });
  };

  const currentYear = new Date().getFullYear();
  const federalYears = [...new Set(configs.filter((c) => c.jurisdiction === 'federal').map((c) => c.tax_year))];
  const stateYears = [...new Set(configs.filter((c) => c.jurisdiction === 'state').map((c) => c.tax_year))];
  const ficaYears = [...new Set(ficaConfigs.map((c) => c.tax_year))];
  const latestFederalYear = federalYears.length ? Math.max(...federalYears) : null;
  const latestStateYear = stateYears.length ? Math.max(...stateYears) : null;
  const latestFicaYear = ficaYears.length ? Math.max(...ficaYears) : null;
  const hasFederalCurrent = federalYears.includes(currentYear);
  const hasStateCurrent = stateYears.includes(currentYear);
  const hasFicaCurrent = ficaYears.includes(currentYear);
  const selectedYear = filterYear === 'all' ? null : Number(filterYear);
  const stateFallbackYear = selectedYear === currentYear && !hasStateCurrent ? latestStateYear : null;

  const filteredConfigs = configs.filter(config => {
    const matchesSearch = 
      config.filing_status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      config.state_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      config.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesYear =
      selectedYear === null ||
      config.tax_year === selectedYear ||
      (stateFallbackYear && config.jurisdiction === 'state' && config.tax_year === stateFallbackYear);
    const matchesJurisdiction = filterJurisdiction === 'all' || config.jurisdiction === filterJurisdiction;
    return matchesSearch && matchesYear && matchesJurisdiction;
  });

  const availableYears = [...new Set(configs.map(c => c.tax_year))]
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Tax Rate Management</h1>
          <p className="text-slate-500 mt-1">
            Configure federal and state tax rates for Section 125 calculations
          </p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Tax Configuration
        </Button>
      </div>

      <Card className="border-emerald-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            Annual Tax Table Update
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Automatically refreshes federal tax brackets and FICA figures for the current year via API Ninjas
            (IRS.gov itself has no public data API, so this mirrors the same published IRS figures through the
            configured provider). Runs automatically once a year — by default every January 15th — and can also
            be triggered manually here at any time.
          </p>
          <Button onClick={handleAnnualUpdate} disabled={annualUpdating}>
            {annualUpdating ? <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Run Annual Tax Table Update Now
          </Button>
          {annualUpdateResult && (
            <div className="text-xs text-slate-600 bg-slate-50 border rounded-lg p-3 space-y-1">
              <p><strong>Year:</strong> {annualUpdateResult.year}</p>
              <p>
                <strong>Federal brackets:</strong>{' '}
                {'created' in (annualUpdateResult.federal || {})
                  ? `${annualUpdateResult.federal.created} created, ${annualUpdateResult.federal.updated} updated`
                  : annualUpdateResult.federal?.skipped}
              </p>
              <p>
                <strong>FICA:</strong>{' '}
                {('created' in (annualUpdateResult.fica || {}) || 'updated' in (annualUpdateResult.fica || {}))
                  ? (annualUpdateResult.fica.created ? 'Created' : 'Updated')
                  : annualUpdateResult.fica?.skipped}
              </p>
              <p>
                <strong>State rates (Tax Foundation):</strong>{' '}
                {'totalStates' in (annualUpdateResult.taxFoundation || {})
                  ? `${annualUpdateResult.taxFoundation.created} created, ${annualUpdateResult.taxFoundation.updated} updated across ${annualUpdateResult.taxFoundation.totalStates} states (data year: ${annualUpdateResult.taxFoundation.dataYear})`
                  : annualUpdateResult.taxFoundation?.skipped}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-500" />
            Import via API Ninjas (Recommended)
          </CardTitle>
          <div className="flex items-center gap-2">
            {!hasFederalCurrent && (
              <Badge className="bg-amber-100 text-amber-800">Missing {currentYear}</Badge>
            )}
            <Button variant="ghost" size="sm" onClick={() => setNinjasOpen((open) => !open)}>
              {ninjasOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
        </CardHeader>
        {ninjasOpen && (
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Uses the API Ninjas Income Tax API to import federal and optional state brackets.
              Configure your API key in System Configuration → Tax Rates. Documentation:
              <a className="text-emerald-600 hover:underline ml-1" href="https://api-ninjas.com/api/incometax" target="_blank" rel="noreferrer">api-ninjas.com/api/incometax</a>
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <Input
                placeholder="Tax year (e.g., 2026)"
                value={ninjasYear}
                onChange={(e) => setNinjasYear(e.target.value)}
              />
              <Input
                placeholder="Regions (e.g., federal,CA,NY)"
                value={ninjasRegions}
                onChange={(e) => setNinjasRegions(e.target.value)}
              />
              <Button onClick={handleImportApiNinjas} disabled={ninjasImporting}>
                {ninjasImporting ? <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" /> : <Database className="w-4 h-4 mr-2" />}
                Import from API Ninjas
              </Button>
            </div>
            {!hasFederalCurrent && latestFederalYear && (
              <p className="text-xs text-amber-700">
                Latest federal data: {latestFederalYear}. {currentYear > latestFederalYear ? `The ${currentYear} release may not be available yet.` : ''}
              </p>
            )}
            <p className="text-xs text-slate-500">
              Tip: Use <strong>federal</strong> to import only federal rates. Add state codes like <strong>CA,NY</strong> to include states.
              State data and FICA may require a premium API Ninjas plan.
            </p>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-500" />
            Import State Rates from Tax Foundation
          </CardTitle>
          <div className="flex items-center gap-2">
            {!hasStateCurrent && (
              <Badge className="bg-amber-100 text-amber-800">Missing {currentYear}</Badge>
            )}
            <Button variant="ghost" size="sm" onClick={() => setTaxFoundationOpen((open) => !open)}>
              {taxFoundationOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
        </CardHeader>
        {taxFoundationOpen && (
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Imports the latest published state brackets from Tax Foundation’s data download.
              The importer will try to auto-detect the newest XLSX download link from the Tax Foundation page.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <Input
                placeholder="Tax year (e.g., 2025)"
                value={taxFoundationYear}
                onChange={(e) => setTaxFoundationYear(e.target.value)}
              />
              <Input
                placeholder="Optional XLSX URL (leave blank to auto-detect)"
                value={taxFoundationUrl}
                onChange={(e) => setTaxFoundationUrl(e.target.value)}
              />
              <Button onClick={handleImportTaxFoundation} disabled={taxFoundationImporting}>
                {taxFoundationImporting ? <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" /> : <Database className="w-4 h-4 mr-2" />}
                Import State Rates
              </Button>
            </div>
            {!hasStateCurrent && latestStateYear && (
              <p className="text-xs text-amber-700">
                Latest state data: {latestStateYear}. {currentYear > latestStateYear ? `The ${currentYear} release may not be available yet.` : ''}
              </p>
            )}
            <div className="text-xs text-slate-500 space-y-1">
              <p>
                Source page: <a className="text-emerald-600 hover:underline" href="https://taxfoundation.org/data/all/state/state-income-tax-rates/" target="_blank" rel="noreferrer">taxfoundation.org/data/all/state/state-income-tax-rates/</a>
              </p>
              <p>
                If auto-detect fails, open the page and copy the "DOWNLOAD XXXX DATA" XLSX link.
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-500" />
            Import FICA from SSA (Federal)
          </CardTitle>
          <div className="flex items-center gap-2">
            {!hasFicaCurrent && (
              <Badge className="bg-amber-100 text-amber-800">Missing {currentYear}</Badge>
            )}
            <Button variant="ghost" size="sm" onClick={() => setFicaOpen((open) => !open)}>
              {ficaOpen ? 'Hide' : 'Show'}
            </Button>
          </div>
        </CardHeader>
        {ficaOpen && (
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Imports FICA using the API Ninjas tax calculator (one call per year), with SSA as a fallback.
              Medicare rates default to 1.45% and can be edited in the FICA tab after import.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <Input
                placeholder="Tax year (e.g., 2026)"
                value={ficaYear}
                onChange={(e) => setFicaYear(e.target.value)}
              />
              {useSsaFallback ? (
                <Input
                  placeholder="SSA CBB URL"
                  value={ficaSsaUrl}
                  onChange={(e) => setFicaSsaUrl(e.target.value)}
                />
              ) : (
                <div className="hidden md:block" />
              )}
              <Button onClick={handleImportFica} disabled={ficaImporting}>
                {ficaImporting ? <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" /> : <Database className="w-4 h-4 mr-2" />}
                Import FICA
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Input
                placeholder="Manual SS rate (e.g., 0.062)"
                value={ficaManualRate}
                onChange={(e) => setFicaManualRate(e.target.value)}
              />
              <Input
                placeholder="Manual wage base (e.g., 184500)"
                value={ficaManualWageBase}
                onChange={(e) => setFicaManualWageBase(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={useSsaFallback}
                onChange={(e) => setUseSsaFallback(e.target.checked)}
              />
              Use SSA HTML fallback if API Ninjas is unavailable
            </label>
            {!hasFicaCurrent && latestFicaYear && (
              <p className="text-xs text-amber-700">
                Latest FICA data: {latestFicaYear}. {currentYear > latestFicaYear ? `The ${currentYear} release may not be available yet.` : ''}
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {/* Seed Data - Smart Prompts */}
      {(!hasFederal || !hasState) && (
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <AlertCircle className="w-5 h-5" />
              Initial Setup Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-amber-800">
              Tax rate data is needed for accurate Section 125 calculations. Seed the data below to get started.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {!hasFederal && <TaxRateSeedData onComplete={loadConfigs} />}
              {!hasState && <AllStatesTaxSeedData onComplete={loadConfigs} />}
            </div>
            
            {(hasFederal && !hasState) && (
              <p className="text-xs text-amber-700 bg-amber-100 p-3 rounded">
                âœ“ Federal rates loaded. Add state rates to enable state tax calculations.
              </p>
            )}
            {(!hasFederal && hasState) && (
              <p className="text-xs text-amber-700 bg-amber-100 p-3 rounded">
                âœ“ State rates loaded. Add federal rates for complete tax calculations.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Calculator className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Configurations</p>
                <p className="text-2xl font-bold text-slate-900">{configs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Federal Years</p>
                <p className="text-2xl font-bold text-slate-900">{federalYears.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">State Years</p>
                <p className="text-2xl font-bold text-slate-900">{stateYears.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by filing status, state, or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterJurisdiction} onValueChange={setFilterJurisdiction}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Jurisdiction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jurisdictions</SelectItem>
                <SelectItem value="federal">Federal</SelectItem>
                <SelectItem value="state">State</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Configs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tax Configurations</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Year</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Filing Status</TableHead>
                <TableHead>Std. Deduction</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No tax configurations found
                  </TableCell>
                </TableRow>
              ) : (
                filteredConfigs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell className="font-medium">
                      {config.tax_year}
                      {stateFallbackYear && config.jurisdiction === 'state' && config.tax_year === stateFallbackYear && selectedYear === currentYear ? ' (fallback)' : ''}
                    </TableCell>
                    <TableCell>
                      <Badge variant={config.jurisdiction === 'federal' ? 'default' : 'secondary'}>
                        {config.jurisdiction}
                      </Badge>
                    </TableCell>
                    <TableCell>{config.state_code || 'N/A'}</TableCell>
                    <TableCell className="capitalize">{config.filing_status?.replace('_', ' ')}</TableCell>
                    <TableCell>
                      ${typeof config.standard_deduction === 'number' ? config.standard_deduction.toLocaleString() : '0'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(config)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDeleteConfig(config)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingConfig ? 'Edit Tax Configuration' : 'Add Tax Configuration'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="brackets">Tax Brackets</TabsTrigger>
              <TabsTrigger value="fica">FICA</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Tax Year</label>
                  <Input
                    type="number"
                    value={formData.tax_year}
                    onChange={(e) => setFormData({...formData, tax_year: parseInt(e.target.value)})}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">Jurisdiction</label>
                  <Select value={formData.jurisdiction} onValueChange={(v) => setFormData({...formData, jurisdiction: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="federal">Federal</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.jurisdiction === 'state' && (
                  <div>
                    <label className="text-sm font-medium">State</label>
                    <Select value={formData.state_code} onValueChange={(v) => setFormData({...formData, state_code: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(state => (
                          <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium">Filing Status</label>
                  <Select value={formData.filing_status} onValueChange={(v) => setFormData({...formData, filing_status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="head_of_household">Head of Household</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Standard Deduction</label>
                  <Input
                    type="number"
                    value={formData.standard_deduction}
                    onChange={(e) => setFormData({...formData, standard_deduction: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Optional admin notes..."
                />
              </div>
            </TabsContent>
            
            <TabsContent value="brackets" className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-500">Configure progressive tax brackets</p>
                <Button size="sm" onClick={addBracket}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Bracket
                </Button>
              </div>
              
              <div className="space-y-3">
                {formData.tax_brackets.map((bracket, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="flex items-end gap-3">
                        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div>
                            <label className="text-xs font-medium">Min Income</label>
                            <Input
                              type="number"
                              value={bracket.min_income}
                              onChange={(e) => updateBracket(index, 'min_income', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium">Max Income</label>
                            <Input
                              type="number"
                              value={bracket.max_income}
                              onChange={(e) => updateBracket(index, 'max_income', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium">Rate</label>
                            <Input
                              type="number"
                              step="0.001"
                              value={bracket.rate}
                              onChange={(e) => updateBracket(index, 'rate', e.target.value)}
                              placeholder="e.g., 0.22"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium">Base Tax</label>
                            <Input
                              type="number"
                              value={bracket.base_tax}
                              onChange={(e) => updateBracket(index, 'base_tax', e.target.value)}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBracket(index)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {formData.tax_brackets.length === 0 && (
                  <p className="text-center text-slate-500 py-8">
                    No tax brackets defined. Click "Add Bracket" to get started.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="fica" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Social Security Rate</label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={ficaForm.social_security_rate}
                    onChange={(e) => setFicaForm({ ...ficaForm, social_security_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Social Security Wage Base</label>
                  <Input
                    type="number"
                    value={ficaForm.social_security_wage_base}
                    onChange={(e) => setFicaForm({ ...ficaForm, social_security_wage_base: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Medicare Rate</label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={ficaForm.medicare_rate}
                    onChange={(e) => setFicaForm({ ...ficaForm, medicare_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Additional Medicare Rate</label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={ficaForm.additional_medicare_rate}
                    onChange={(e) => setFicaForm({ ...ficaForm, additional_medicare_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Additional Medicare Threshold</label>
                  <Input
                    type="number"
                    value={ficaForm.additional_medicare_threshold}
                    onChange={(e) => setFicaForm({ ...ficaForm, additional_medicare_threshold: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Notes</label>
                  <Input
                    value={ficaForm.notes}
                    onChange={(e) => setFicaForm({ ...ficaForm, notes: e.target.value })}
                    placeholder="Optional FICA notes..."
                  />
                </div>
              </div>
            </TabsContent>
            
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600">
              {editingConfig ? 'Update' : 'Create'} Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}